"use client"
import { MAX_TRUST_SCORE } from "@/lib/trust/constants"
import Link from "next/link"
import { useEffect, useRef, useState } from "react"
import Icon from "@/components/ui/Icon"
import { sellerDisplayName } from "@/lib/accounts/display-name"
import SellerLink from "@/components/SellerLink"

const SUBURB_COORDS: Record<string, { lat: number; lng: number }> = {
  // ── Harare ────────────────────────────────────────────────────────────
  "harare":                 { lat: -17.831, lng: 31.052 },
  "borrowdale":             { lat: -17.747, lng: 31.101 },
  "borrowdale brooke":      { lat: -17.743, lng: 31.115 },
  "mt pleasant":            { lat: -17.779, lng: 30.997 },
  "mount pleasant":         { lat: -17.779, lng: 30.997 },
  "avondale":               { lat: -17.809, lng: 31.019 },
  "avondale west":          { lat: -17.808, lng: 31.008 },
  "highlands":              { lat: -17.799, lng: 31.064 },
  "northgate":              { lat: -17.750, lng: 30.972 },
  "northgate heights":      { lat: -17.748, lng: 30.975 },
  "glen lorne":             { lat: -17.763, lng: 31.124 },
  "greendale":              { lat: -17.830, lng: 31.098 },
  "eastlea":                { lat: -17.844, lng: 31.063 },
  "hatfield":               { lat: -17.858, lng: 31.050 },
  "mabelreign":             { lat: -17.829, lng: 30.993 },
  "westgate":               { lat: -17.851, lng: 30.982 },
  "chisipite":              { lat: -17.758, lng: 31.110 },
  "newlands":               { lat: -17.822, lng: 31.046 },
  "madokero":               { lat: -17.794, lng: 30.955 },
  "msasa":                  { lat: -17.833, lng: 31.100 },
  "waterfalls":             { lat: -17.882, lng: 31.038 },
  "glen view":              { lat: -17.877, lng: 31.000 },
  "dzivarasekwa":           { lat: -17.843, lng: 30.954 },
  "kuwadzana":              { lat: -17.834, lng: 30.942 },
  "budiriro":               { lat: -17.893, lng: 30.996 },
  "sunridge":               { lat: -17.870, lng: 31.088 },
  "ruwa":                   { lat: -17.899, lng: 31.247 },
  "bluffhill":              { lat: -17.795, lng: 30.976 },
  "mbare":                  { lat: -17.869, lng: 31.040 },
  "southerton":             { lat: -17.860, lng: 31.020 },
  "workington":             { lat: -17.850, lng: 31.001 },
  "norton":                 { lat: -17.883, lng: 30.700 },
  "chitungwiza":            { lat: -17.996, lng: 31.074 },
  "epworth":                { lat: -17.907, lng: 31.127 },
  "pomona":                 { lat: -17.768, lng: 31.078 },
  "tynwald":                { lat: -17.826, lng: 30.967 },
  "belvedere":              { lat: -17.836, lng: 31.020 },
  "ashdown park":           { lat: -17.800, lng: 31.000 },
  "milton park":            { lat: -17.826, lng: 31.032 },
  "strathaven":             { lat: -17.840, lng: 31.072 },
  "vainona":                { lat: -17.749, lng: 31.063 },
  // ── Bulawayo ──────────────────────────────────────────────────────────
  "bulawayo":               { lat: -20.150, lng: 28.586 },
  "suburbs":                { lat: -20.144, lng: 28.567 },
  "burnside":               { lat: -20.171, lng: 28.556 },
  "hillside":               { lat: -20.174, lng: 28.613 },
  "queens park":            { lat: -20.137, lng: 28.610 },
  "pumula":                 { lat: -20.201, lng: 28.546 },
  "nkulumane":              { lat: -20.148, lng: 28.527 },
  "bellevue":               { lat: -20.155, lng: 28.586 },
  "famona":                 { lat: -20.180, lng: 28.557 },
  "matsheumhlope":          { lat: -20.163, lng: 28.600 },
  "waterford":              { lat: -20.188, lng: 28.597 },
  "woodville":              { lat: -20.157, lng: 28.560 },
  "selborne park":          { lat: -20.145, lng: 28.605 },
  "luveve":                 { lat: -20.174, lng: 28.520 },
  "emganwini":              { lat: -20.225, lng: 28.545 },
  "makokoba":               { lat: -20.150, lng: 28.576 },
  "fort rixon":             { lat: -20.092, lng: 28.583 },
  "mahatshula":             { lat: -20.200, lng: 28.576 },
  "cowdray park":           { lat: -20.122, lng: 28.520 },
  // ── Mutare ────────────────────────────────────────────────────────────
  "mutare":                 { lat: -18.975, lng: 32.669 },
  "hobhouse":               { lat: -18.963, lng: 32.650 },
  "morningside":            { lat: -18.960, lng: 32.660 },
  "chikanga":               { lat: -18.985, lng: 32.695 },
  "dangamvhu":              { lat: -18.997, lng: 32.680 },
  "sakubva":                { lat: -18.975, lng: 32.668 },
  "greenside":              { lat: -18.968, lng: 32.642 },
  "palmerstone":            { lat: -18.957, lng: 32.643 },
  "murambi":                { lat: -18.950, lng: 32.656 },
  "fern valley":            { lat: -18.980, lng: 32.650 },
  "yeovil":                 { lat: -18.987, lng: 32.636 },
  // ── Gweru ─────────────────────────────────────────────────────────────
  "gweru":                  { lat: -19.455, lng: 29.817 },
  "senga":                  { lat: -19.440, lng: 29.836 },
  "mkoba":                  { lat: -19.473, lng: 29.806 },
  "mambo":                  { lat: -19.452, lng: 29.808 },
  "ascot":                  { lat: -19.434, lng: 29.802 },
  "riverside":              { lat: -19.443, lng: 29.823 },
  "windsor park":           { lat: -19.448, lng: 29.835 },
  "montrose":               { lat: -19.455, lng: 29.795 },
  "southdowns":             { lat: -19.470, lng: 29.823 },
  // ── Victoria Falls ────────────────────────────────────────────────────
  "victoria falls":         { lat: -17.931, lng: 25.832 },
  "chinotimba":             { lat: -17.943, lng: 25.836 },
  "mkhosana":               { lat: -17.952, lng: 25.845 },
  "manyika":                { lat: -17.935, lng: 25.820 },
  // ── Masvingo ──────────────────────────────────────────────────────────
  "masvingo":               { lat: -20.074, lng: 30.832 },
  "mucheke":                { lat: -20.090, lng: 30.838 },
  "rhodene":                { lat: -20.065, lng: 30.825 },
  "runyararo":              { lat: -20.085, lng: 30.826 },
  "regans park":            { lat: -20.063, lng: 30.840 },
  // ── Kwekwe ────────────────────────────────────────────────────────────
  "kwekwe":                 { lat: -18.929, lng: 29.815 },
  "newtown":                { lat: -18.921, lng: 29.808 },
  "mbizo":                  { lat: -18.940, lng: 29.822 },
  "amaveni":                { lat: -18.948, lng: 29.808 },
  // ── Chinhoyi ──────────────────────────────────────────────────────────
  "chinhoyi":               { lat: -17.361, lng: 30.197 },
  "cold comfort":           { lat: -17.370, lng: 30.195 },
  "chikonohono":            { lat: -17.352, lng: 30.201 },
  // ── Bindura ───────────────────────────────────────────────────────────
  "bindura":                { lat: -17.301, lng: 31.328 },
  "chiwaridzo":             { lat: -17.310, lng: 31.335 },
  "chipadze":               { lat: -17.295, lng: 31.322 },
  // ── Other towns ───────────────────────────────────────────────────────
  "marondera":              { lat: -18.185, lng: 31.551 },
  "kadoma":                 { lat: -18.335, lng: 29.912 },
  "chegutu":                { lat: -18.141, lng: 30.147 },
  "zvishavane":             { lat: -20.336, lng: 30.034 },
  "hwange":                 { lat: -18.368, lng: 26.499 },
  "kariba":                 { lat: -16.519, lng: 28.805 },
  "beitbridge":             { lat: -22.214, lng: 30.000 },
}

function getCoords(suburb: string, city: string) {
  const s = (suburb ?? "").toLowerCase().trim()
  const c = (city ?? "").toLowerCase().trim()
  return SUBURB_COORDS[s] ?? SUBURB_COORDS[c] ?? { lat: -17.831, lng: 31.052 }
}

function formatPrice(l: any) {
  if (!l.price) return "-"
  return l.priceType === "RENT" ? `$${l.price.toLocaleString()}/mo` : `$${l.price.toLocaleString()}`
}

declare global {
  interface Window {
    google: any
    __mapsLoaded: boolean
  }
}

export default function MapPage() {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstance = useRef<any>(null)
  const markersRef = useRef<any[]>([])
  const [listings, setListings] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [mapsReady, setMapsReady] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)

  const sel = listings.find(l => l.id === selected)

  useEffect(() => {
    fetch("/api/listings?pageSize=50")
      .then(r => r.json())
      .then(d => { setListings(d.data ?? []); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (window.__mapsLoaded) { setMapsReady(true); return }
    const script = document.createElement("script")
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&v=weekly`
    script.async = true
    script.onload = () => { window.__mapsLoaded = true; setMapsReady(true) }
    document.head.appendChild(script)
  }, [])

  useEffect(() => {
    if (!mapsReady || !mapRef.current) return
    if (mapInstance.current) return
    mapInstance.current = new window.google.maps.Map(mapRef.current, {
      center: { lat: -17.831, lng: 31.052 },
      zoom: 12,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: [
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        { featureType: "transit", stylers: [{ visibility: "off" }] },
      ],
    })
  }, [mapsReady])

  useEffect(() => {
    if (!mapInstance.current || listings.length === 0) return
    markersRef.current.forEach(m => m.setMap(null))
    markersRef.current = []

    listings.forEach(l => {
      const pos = getCoords(l.suburb, l.city)
      const label = formatPrice(l)

      const markerEl = document.createElement("div")
      markerEl.style.cssText = `
        background: ${l.isFeatured ? "#C8963E" : "#1B2D54"};
        color: ${l.isFeatured ? "#1B2D54" : "#FDFBF7"};
        border: 2px solid white;
        border-radius: 10px 10px 10px 0;
        padding: 5px 10px;
        font-size: 12px;
        font-weight: 700;
        white-space: nowrap;
        cursor: pointer;
        box-shadow: 0 2px 10px rgba(0,0,0,0.35);
        font-family: system-ui, sans-serif;
        transition: all 0.15s;
      `
      markerEl.textContent = label + (l.isFeatured ? " ★" : "")

      let marker: any
      if (window.google.maps.marker?.AdvancedMarkerElement) {
        marker = new window.google.maps.marker.AdvancedMarkerElement({
          map: mapInstance.current,
          position: pos,
          content: markerEl,
          title: l.title,
        })
        marker.addListener("click", () => setSelected((prev: string | null) => prev === l.id ? null : l.id))
      } else {
        marker = new window.google.maps.Marker({
          map: mapInstance.current,
          position: pos,
          title: l.title,
          label: {
            text: label,
            color: "#FDFBF7",
            fontSize: "11px",
            fontWeight: "700",
          },
          icon: {
            path: "M -20,-10 L 20,-10 Q 22,-10 22,-8 L 22,8 Q 22,10 20,10 L -20,10 Q -22,10 -22,8 L -22,-8 Q -22,-10 -20,-10 Z",
            fillColor: l.isFeatured ? "#C8963E" : "#1B2D54",
            fillOpacity: 1,
            strokeColor: "white",
            strokeWeight: 2,
            scale: 1,
            anchor: new window.google.maps.Point(-22, 10),
          },
        })
        marker.addListener("click", () => setSelected((prev: string | null) => prev === l.id ? null : l.id))
      }

      markersRef.current.push(marker)
    })
  }, [listings, mapsReady])

  useEffect(() => {
    if (!mapInstance.current || !sel) return
    const pos = getCoords(sel.suburb, sel.city)
    mapInstance.current.panTo(pos)
  }, [selected])

  return (
    <div style={{ height: "calc(100vh - 68px)", display: "grid", gridTemplateColumns: selected ? "1fr 380px" : "1fr", overflow: "hidden", transition: "grid-template-columns 0.3s ease" }}>
      <div style={{ position: "relative", overflow: "hidden" }}>
        <div ref={mapRef} style={{ width: "100%", height: "100%" }} />

        <div style={{ position: "absolute", top: 16, left: 16, zIndex: 10, background: "rgba(253,251,247,0.96)", backdropFilter: "blur(8px)", borderRadius: 10, padding: "8px 14px", fontSize: 13, fontWeight: 600, color: "var(--navy)", border: "1px solid var(--line)", boxShadow: "0 2px 12px rgba(0,0,0,0.12)" }}>
          {loading ? "Loading listings…" : `${listings.length} listing${listings.length !== 1 ? "s" : ""} on map`}
        </div>

        <div style={{ position: "absolute", bottom: 24, left: 16, zIndex: 10, display: "flex", gap: 8 }}>
          <Link href="/listings" style={{ padding: "10px 18px", background: "rgba(253,251,247,0.96)", backdropFilter: "blur(8px)", border: "1px solid var(--line)", borderRadius: 10, fontSize: 13, fontWeight: 600, color: "var(--navy)", textDecoration: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.12)" }}>List view</Link>
          {selected && <button onClick={() => setSelected(null)} style={{ padding: "10px 18px", background: "rgba(253,251,247,0.96)", backdropFilter: "blur(8px)", border: "1px solid var(--line)", borderRadius: 10, fontSize: 13, fontWeight: 600, color: "var(--muted)", cursor: "pointer", fontFamily: "inherit", boxShadow: "0 2px 8px rgba(0,0,0,0.12)" }}>Clear selection</button>}
        </div>
      </div>

      {selected && sel && (
        <div style={{ background: "var(--cream)", borderLeft: "1px solid var(--line)", overflowY: "auto", padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <span style={{ fontFamily: "Fraunces, serif", fontSize: 15, fontWeight: 600, color: "var(--navy)" }}>Selected property</span>
            <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", display: "flex" }}><Icon name="X" size={18} /></button>
          </div>

          <div style={{ height: 160, background: sel.photos?.[0]?.url ? `var(--cream-warm) url(${sel.photos[0].url}) no-repeat center/contain` : sel.priceType === "RENT" ? "linear-gradient(135deg,#3d8b5f,#1B2D54)" : "linear-gradient(135deg,#3a4f7a,#1B2D54)", borderRadius: 12, marginBottom: 16, position: "relative" }}>
            {sel.isFeatured && <span style={{ position: "absolute", top: 10, left: 10, background: "#C8963E", color: "#1B2D54", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 5, textTransform: "uppercase" }}>Featured</span>}
          </div>

          <div style={{ background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 14, padding: 20, marginBottom: 16 }}>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: "var(--navy)", marginBottom: 4 }}>{formatPrice(sel)}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>{sel.title}</div>
            <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 12 }}>{sel.suburb}, {sel.city}</div>
            <div style={{ display: "flex", gap: 12, fontSize: 12, color: "var(--muted)", marginBottom: 12 }}>
              {sel.beds > 0 && <span>{sel.beds} beds</span>}
              {sel.baths > 0 && <span>{sel.baths} baths</span>}
              {sel.floorArea > 0 && <span>{sel.floorArea}m²</span>}
            </div>
            {sel.trustScore && <div className="trust-score" style={{ marginBottom: 16 }}>Trust Score {sel.trustScore.totalScore}/{MAX_TRUST_SCORE}</div>}
            {sel.agent?.name && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
                <div style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--navy)", display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700, color: "var(--cream)" }}>{sellerDisplayName(sel.agent).substring(0, 2).toUpperCase()}</div>
                <div>
                  <SellerLink agent={sel.agent} style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)", textDecoration: "none" }}>{sellerDisplayName(sel.agent)}</SellerLink>
                  {/* Was unconditional — shown for every seller regardless of KYC. */}
                  {sel.trustScore?.idVerified && <div style={{ fontSize: 11, color: "var(--green-text)", fontWeight: 600 }}>Verified Agent</div>}
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <Link href={"/listings/" + sel.id} className="btn-navy" style={{ flex: 1, textAlign: "center", fontSize: 13 }}>View listing</Link>
              {/* Counted server-side redirect, not a raw wa.me. */}
              <a href={"/api/go/whatsapp?listing=" + encodeURIComponent(sel.id)} target="_blank" rel="noopener noreferrer" style={{ flex: 1, padding: "10px", background: "#075E54", color: "white", borderRadius: 8, fontWeight: 700, fontSize: 13, textAlign: "center", display: "block", textDecoration: "none" }}>WhatsApp</a>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
